import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';

const STATUS_COLORS = {
  pending: '#ffc107',
  active: '#0066cc',
  matched: '#17a2b8',
  confirmed: '#28a745',
  in_progress: '#6f42c1',
  completed: '#28a745',
  cancelled: '#dc3545'
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Jobs' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'matched', label: 'Matched' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    cancelled: 0
  });

  useEffect(() => {
    loadJobs();
  }, [filter]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api(`/admin/requests${params}`);

      // Filter for crew/job requests (not just mooring)
      const jobData = (response.data || []).filter(r =>
        r.request_category === 'crew' || r.crew_department || r.crew_position
      );

      setJobs(jobData);

      // Calculate stats
      const allJobs = response.data || [];
      setStats({
        total: allJobs.filter(r => r.request_category === 'crew' || r.crew_department).length,
        active: allJobs.filter(r => ['active', 'matched', 'confirmed', 'in_progress'].includes(r.status) && (r.request_category === 'crew' || r.crew_department)).length,
        completed: allJobs.filter(r => r.status === 'completed' && (r.request_category === 'crew' || r.crew_department)).length,
        cancelled: allJobs.filter(r => r.status === 'cancelled' && (r.request_category === 'crew' || r.crew_department)).length,
      });
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (job.port_name || '').toLowerCase().includes(searchLower) ||
      (job.yacht_name || '').toLowerCase().includes(searchLower) ||
      (job.crew_department || '').toLowerCase().includes(searchLower) ||
      (job.crew_position || '').toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{
          width: 40, height: 40,
          border: '3px solid #e0e0e0', borderTop: '3px solid #0066cc',
          borderRadius: '50%', animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#666' }}>Loading jobs...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Jobs</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Crew placement and job requests</p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Total Jobs</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e' }}>{stats.total}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Active</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0066cc' }}>{stats.active}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Completed</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#28a745' }}>{stats.completed}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Cancelled</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#dc3545' }}>{stats.cancelled}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24, padding: 16,
        background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <input
          type="text"
          placeholder="Search jobs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '10px 14px', border: '1px solid #ddd',
            borderRadius: 8, fontSize: 14
          }}
        />
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            padding: '10px 14px', border: '1px solid #ddd',
            borderRadius: 8, fontSize: 14, minWidth: 150
          }}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Jobs List */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {filteredJobs.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Position</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Department</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Location</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Yacht</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Contract</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Start Date</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Applications</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job, idx) => (
                <tr key={job.id} style={{ borderBottom: idx < filteredJobs.length - 1 ? '1px solid #eee' : 'none' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{job.crew_position || 'Crew Member'}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>#{job.id.substring(0, 8)}</div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    {job.crew_department || 'General'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    {job.port_name || 'N/A'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    {job.yacht_name || 'N/A'}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 500,
                      background: job.contract_type === 'permanent' ? '#0066cc15' : '#ffc10715',
                      color: job.contract_type === 'permanent' ? '#0066cc' : '#ffc107',
                    }}>
                      {job.contract_type || 'Temporary'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    {formatDate(job.start_date || job.scheduled_date)}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      display: 'inline-block', padding: '4px 10px', borderRadius: 16,
                      fontSize: 11, fontWeight: 500,
                      background: `${STATUS_COLORS[job.status]}15`,
                      color: STATUS_COLORS[job.status]
                    }}>
                      {job.status}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 14 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 28, height: 28, borderRadius: '50%',
                      background: '#f0f0f0', fontWeight: 600, fontSize: 12
                    }}>
                      {job.application_count || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 60, textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💼</div>
            <p style={{ fontSize: 16, marginBottom: 8 }}>No jobs found</p>
            <p style={{ fontSize: 13 }}>
              {filter !== 'all' ? 'Try changing your filters' : 'Crew job listings will appear here'}
            </p>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div style={{
        marginTop: 24, padding: 20,
        background: '#e8f4fd', borderRadius: 12,
        border: '1px solid #b6d4fe'
      }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#0066cc', fontSize: 14 }}>About Jobs</h4>
        <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
          This section shows crew placement jobs - positions for deck crew, stewardesses, engineers, chefs, and other yacht crew roles.
          For mooring assistance requests, see the <Link to="/requests" style={{ color: '#0066cc' }}>Requests</Link> page.
        </p>
      </div>
    </div>
  );
}

export default Jobs;
